// DEVNOTE may be able to re-use for site when necessasry
// todo different, we need to use the template to get the path(s), but return the values
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EMPTY_ARRAY, EMPTY_OBJECT, EMPTY_STRING, OPTION_TYPES } from './constants';

dayjs.extend(utc);

export function formatDateTime(value, emptyValue = '-') {
    if (!value) {
        return emptyValue;
    }

    return dayjs.utc(value).local().format('MMM D, YYYY h:mm A');
}

export function filterDataByTemplate(template, data, parentPath = []) {
    const filteredData = {};
    for (const [key, item] of Object.entries(template)) {
        const isGroup = typeof item === 'object' && item?.type === 'GROUP' && item?.properties;
        const path = [...parentPath.slice(0), key];
        filteredData[key] = isGroup
            ? filterDataByTemplate(item.properties, data, path)
            : get(data, path);
    }
    return filteredData;
}

export const calcDefaultValue = (itemTemplate) => {
    if (itemTemplate === 'STRING') {
        return null;
    }
    if (itemTemplate?.hasOwnProperty('defaultValue')) {
        return itemTemplate.defaultValue;
    }
    if (typeof itemTemplate === 'object') {
        if (itemTemplate.type === OPTION_TYPES.STRING) {
            return EMPTY_STRING;
        }
        if (itemTemplate.type === OPTION_TYPES.DOMAIN) {
            return null;
        }
        if (itemTemplate.type === OPTION_TYPES.AUTH_CONTEXT) {
            return EMPTY_OBJECT;
        }
        if (itemTemplate.type === OPTION_TYPES.DATE) {
            return null;
        }
        if (itemTemplate.type === OPTION_TYPES.BOOL) {
            return false;
        }
        if (itemTemplate.type === OPTION_TYPES.MULTISELECT) {
            return EMPTY_ARRAY;
        }
        if (itemTemplate.type === OPTION_TYPES.RADIO_GROUP) {
            return null;
        }
        if (itemTemplate.type === OPTION_TYPES.SELECT) {
            return null;
        }
        if (itemTemplate.type === OPTION_TYPES.META) {
            const { type, ...metaData } = itemTemplate;
            return metaData;
        }
        if (itemTemplate.type === OPTION_TYPES.LEAGUE_DATA) {
            return EMPTY_OBJECT;
        }
    }
    return null;
};

export function normalizeValue(value, fieldTemplate) {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    switch (fieldTemplate?.type) {
        case OPTION_TYPES.DATE:
            return formatDateTime(value);
        case OPTION_TYPES.SELECT:
        case OPTION_TYPES.MULTISELECT:
            const { options = [] } = fieldTemplate;
            return normalizeSelectValue(value, options);
        case OPTION_TYPES.BOOL:
            return value ? 'Yes' : 'No';
        default:
            return String(value);
    }
}

export function normalizeSelectValue(value, options) {
    const dataForSelect = options.map((o) => {
        if (typeof o === 'string' || typeof o === 'number') {
            // turn into an object meeting the appropriate criteria
            return { name: o, displayKey: o.toString() };
        }
        if (!o.name) {
            o.name = o.displayKey;
        }
        if (!o.displayKey) {
            o.displayKey = o.name;
        }
        return o;
    });
    const valueForLookup = Array.isArray(value) ? value : [value];
    const selections = dataForSelect.filter((d) => valueForLookup.includes(d.name));
    const displaySelections = selections.length
        ? selections.map((s) => s.displayKey).join(', ')
        : valueForLookup.length
        ? valueForLookup.join(',')
        : `-`;
    return displaySelections;
}

export function generateDefaultData(group = {}) {
    const newObj = {};
    for (const [key, item] of Object.entries(group)) {
        if (typeof item === 'object' && item?.type === 'GROUP' && item?.properties) {
            newObj[key] = generateDefaultData(item.properties);
        } else {
            newObj[key] = calcDefaultValue(item);
        }
    }
    return newObj;
}

const objectTypes = [
    OPTION_TYPES.SELECT,
    OPTION_TYPES.MULTISELECT,
    //OPTION_TYPES.AUTH_REQUIREMENTS,
    OPTION_TYPES.AUTH_CONTEXT,
    OPTION_TYPES.META,
    OPTION_TYPES.LEAGUE_DATA,
];

export function isGroupDirty(group, data, stagedEdits, parentPath = []) {
    return Object.entries(group).some(([key, item]) => {
        if (typeof item === 'object' && item?.type === 'GROUP' && item?.properties) {
            const path = [...parentPath.slice(0), key];
            return isGroupDirty(item.properties, data, stagedEdits, path);
        }
        const path = [...parentPath.slice(0), key].join('.');
        const isDirty = isItemDirty(item, path, data, stagedEdits);
        // if (isDirty) {
        //     console.log(item, path, data, stagedEdits);
        // }
        return isDirty;
    });
}

export function isItemDirty(itemTemplate, path, origData, stagedEdits) {
    // this is mainly to handle the OPTION_TYPES.META case but I think it applies generally
    if (itemTemplate?.hidden) {
        return false;
    }
    const defaultValue = calcDefaultValue(itemTemplate);
    const origValue = get(origData, path, defaultValue);
    const stagedValue = get(stagedEdits, path, defaultValue);

    if (objectTypes.includes(itemTemplate.type)) {
        return !isEqual(origValue, stagedValue);
    }
    return stagedValue !== origValue;
}

export function calculateMultiItemViewData(group = {}, parentPath = [], allData) {
    const newObj = {};
    for (const [key, item] of Object.entries(group)) {
        if (typeof item === 'object' && item?.type === 'GROUP' && item?.properties) {
            const path = [...parentPath.slice(0), key];
            newObj[key] = calculateMultiItemViewData(item.properties, path, allData);
        } else {
            const path = [...parentPath.slice(0), key].join('.');
            newObj[key] = calcMultiItemViewValue(item, path, allData);
        }
    }
    return newObj;
}

export function calcMultiItemViewValue(itemTemplate, path, allData) {
    if (itemTemplate?.hidden) {
        return false;
    }
    // todo cleanup exactly logic here (when to display multiple, how many to display etc etc)
    // not sure we want/need this
    const defaultValue = calcDefaultValue(itemTemplate);
    const dataValuesForAllItems = allData.map((dataForItem) =>
        get(dataForItem, path, defaultValue)
    );
    if (
        itemTemplate === 'STRING' ||
        (typeof itemTemplate === 'object' && itemTemplate.type === OPTION_TYPES.STRING)
    ) {
        return combineStrings(dataValuesForAllItems, 3);
    }
    if (typeof itemTemplate === 'object') {
        if (itemTemplate.type === OPTION_TYPES.DATE) {
            return combineDates(dataValuesForAllItems, 3);
        }
        if (itemTemplate.type === OPTION_TYPES.BOOL) {
            return uniqueOrValue(dataValuesForAllItems, null);
        }
        if (itemTemplate.type === OPTION_TYPES.MULTISELECT) {
            return EMPTY_ARRAY;
        }
        if (itemTemplate.type === OPTION_TYPES.SELECT) {
            //if (itemTemplate.options) return dataForAllItems.filter((i) => i).join(',');
            return combineSelects(dataValuesForAllItems, itemTemplate?.options);
        }
    }
    return null;
}

function combineStrings(values, count) {
    const itemsWithValues = values.filter((i) => i);
    const uniqueValues = uniq(itemsWithValues);
    return filterToCount(uniqueValues, count);
}

function combineDates(values, count) {
    const itemsWithValues = values.filter((i) => i);
    const uniqueValues = uniq(itemsWithValues.map((d) => dayjs(d).format('YYYY-MM-DD')));
    return filterToCount(uniqueValues, count);
}

function combineSelects(values, optionValues) {
    //option values can be an array of strings ('user','poweruser') or objects with name/displayKey
    if (!values?.length) {
        return null;
    }
    let uniqueVals = uniq(flatten(values));
    if (optionValues?.length && optionValues[0]?.displayKey) {
        uniqueVals = uniqueVals
            .map((v) => optionValues.find((o) => o.name === v))
            .map((o) => o.displayKey);
    }

    // else just combine and return
    return filterToCount(uniqueVals, 3);
}

function filterToCount(values, count) {
    if (values.length <= count) {
        return values.join(', ');
    }
    const truncValues = values.slice(0, count);
    truncValues.push(' ... more');
    return truncValues.join(', ');
}

// returns either the only unique value or the value of valueIfDifferent
// used to show a shared value across a set or a default value
function uniqueOrValue(values, valueIfDifferent) {
    const uniqueVals = uniq(values);
    if (uniqueVals?.length === 1) {
        return uniqueVals[0];
    }
    return valueIfDifferent;
}
